import { supabase } from '@/integrations/supabase/client';
import { Capacitor } from '@capacitor/core';

/**
 * Detect the platform the message is being sent from
 */
function detectPlatform(): string {
  const platform = Capacitor.getPlatform();
  if (platform === 'ios') return 'apple';
  if (platform === 'android') return 'android';
  return 'web';
}

export interface Conversation {
  id: string;
  customer_id: string;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  customer?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string;
    avatar_url: string | null;
  };
  unread_count?: number;
  last_message?: ChatMessage;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: string;
  content: string | null;
  is_read: boolean;
  read_at: string | null;
  created_at: string;
  updated_at: string;
  reply_to_message_id?: string | null;
  sent_from_platform?: string | null;
  sender?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    avatar_url: string | null;
    job_title: string | null;
    user_roles?: { role: string }[];
  };
  attachments?: ChatAttachment[];
  /** The message this is a reply to (fetched via join) */
  replied_message?: {
    id: string;
    content: string | null;
    sender_id: string;
    sender?: {
      id: string;
      first_name: string | null;
      last_name: string | null;
    };
  } | null;
}

export interface ChatAttachment {
  id: string;
  message_id: string;
  file_name: string;
  file_url: string;
  file_type: string | null;
  file_size: number | null;
  created_at: string;
}

export interface LinkPreviewData {
  url: string;
  title: string | null;
  description: string | null;
  image_url: string | null;
  site_name: string | null;
  favicon_url: string | null;
}

const linkPreviewCache = new Map<string, { success: boolean; data?: LinkPreviewData | null; error?: string }>();
const linkPreviewInFlight = new Map<string, Promise<{ success: boolean; data?: LinkPreviewData | null; error?: string }>>();

/**
 * Get link preview: from in-memory cache, then chat_link_previews, or via fetch-link-preview edge function.
 * One request per URL (deduplicated in-flight and cached).
 */
export async function getLinkPreview(url: string): Promise<{
  success: boolean;
  data?: LinkPreviewData | null;
  error?: string;
}> {
  const cached = linkPreviewCache.get(url);
  if (cached !== undefined) {
    return cached;
  }

  const inFlight = linkPreviewInFlight.get(url);
  if (inFlight !== undefined) {
    return inFlight;
  }

  const promise = (async () => {
    try {
      const { data: dbCached } = await supabase
        .from('chat_link_previews')
        .select('*')
        .eq('url', url)
        .maybeSingle();

      if (dbCached) {
        const result = { success: true as const, data: dbCached as LinkPreviewData };
        linkPreviewCache.set(url, result);
        return result;
      }

      const { data, error: fetchError } = await supabase.functions.invoke('fetch-link-preview', {
        body: { url },
      });

      if (fetchError) {
        const result = { success: false as const, error: fetchError.message };
        linkPreviewCache.set(url, result);
        return result;
      }
      if (!data?.success) {
        const result = { success: false as const, error: data?.error ?? 'Failed to fetch preview' };
        linkPreviewCache.set(url, result);
        return result;
      }
      const result = { success: true as const, data: data.data as LinkPreviewData };
      linkPreviewCache.set(url, result);
      return result;
    } catch (err) {
      console.error('[chat] getLinkPreview error:', err);
      const result = { success: false as const, error: err instanceof Error ? err.message : 'Unknown error' };
      linkPreviewCache.set(url, result);
      return result;
    } finally {
      linkPreviewInFlight.delete(url);
    }
  })();

  linkPreviewInFlight.set(url, promise);
  return promise;
}

/**
 * Get or create conversation for current customer
 */
export async function getOrCreateCustomerConversation(): Promise<{ data: Conversation | null; error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  // Try to get existing conversation
  const { data: existing, error: fetchError } = await supabase
    .from('conversations')
    .select('*')
    .eq('customer_id', user.id)
    .single();

  if (existing) return { data: existing as Conversation, error: null };

  // Create new conversation if not exists
  if (fetchError?.code === 'PGRST116') {
    const { data: created, error: createError } = await supabase
      .from('conversations')
      .insert({ customer_id: user.id })
      .select()
      .single();

    if (createError) return { data: null, error: createError.message };
    return { data: created as Conversation, error: null };
  }

  return { data: null, error: fetchError?.message || 'Unknown error' };
}

/**
 * Create a conversation with a specific user (for staff/admin)
 */
export async function createConversationWith(userId: string): Promise<{ data: Conversation | null; error: string | null }> {
  // Check if conversation already exists
  const { data: existing } = await supabase
    .from('conversations')
    .select(`
      *,
      customer:users!conversations_customer_id_fkey(id, first_name, last_name, email, avatar_url)
    `)
    .eq('customer_id', userId)
    .single();

  if (existing) return { data: existing as Conversation, error: null };

  // Create new conversation
  const { data: created, error: createError } = await supabase
    .from('conversations')
    .insert({ customer_id: userId })
    .select(`
      *,
      customer:users!conversations_customer_id_fkey(id, first_name, last_name, email, avatar_url)
    `)
    .single();

  if (createError) return { data: null, error: createError.message };
  return { data: created as Conversation, error: null };
}

/**
 * Get a conversation by its ID (for direct navigation)
 */
export async function getConversationById(conversationId: string): Promise<{ data: Conversation | null; error: string | null }> {
  const { data, error } = await supabase
    .from('conversations')
    .select(`
      *,
      customer:users!conversations_customer_id_fkey(id, first_name, last_name, email, avatar_url)
    `)
    .eq('id', conversationId)
    .single();

  if (error) return { data: null, error: error.message };
  return { data: data as Conversation, error: null };
}

/**
 * Get or create conversation for a specific user (for impersonation)
 */
export async function getOrCreateConversationForUser(userId: string): Promise<{ data: Conversation | null; error: string | null }> {
  // Try to get existing conversation
  const { data: existing, error: fetchError } = await supabase
    .from('conversations')
    .select(`
      *,
      customer:users!conversations_customer_id_fkey(id, first_name, last_name, email, avatar_url)
    `)
    .eq('customer_id', userId)
    .single();

  if (existing) return { data: existing as Conversation, error: null };

  // No conversation exists for this user
  if (fetchError?.code === 'PGRST116') {
    return { data: null, error: null };
  }

  return { data: null, error: fetchError?.message || 'Unknown error' };
}

/**
 * Get users for creating new conversations
 * - Admins see all users
 * - Non-admin staff only see their assigned clients
 */
export async function getAllUsersForChat(): Promise<{ data: { id: string; first_name: string | null; last_name: string | null; email: string; avatar_url: string | null }[]; error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: [], error: 'Not authenticated' };

  // Check if user is admin
  const { data: adminRole } = await supabase
    .from('user_roles')
    .select('role')
    .eq('user_id', user.id)
    .in('role', ['admin', 'super_admin'])
    .limit(1)
    .single();

  if (adminRole) {
    // Admin: show all users
    const { data, error } = await supabase
      .from('users')
      .select('id, first_name, last_name, email, avatar_url')
      .eq('is_active', true)
      .order('first_name', { ascending: true });

    if (error) return { data: [], error: error.message };
    return { data: data || [], error: null };
  }

  // Non-admin staff: only show assigned clients
  // First, check if staff is a PBM (via team_personal_brand_managers)
  const { data: pbmRecord } = await supabase
    .from('team_personal_brand_managers')
    .select('id')
    .eq('user_id', user.id)
    .limit(1)
    .single();

  // Get clients assigned to this staff as PBM
  let pbmClients: { id: string; first_name: string | null; last_name: string | null; email: string; avatar_url: string | null }[] = [];
  if (pbmRecord) {
    const { data } = await supabase
      .from('users')
      .select('id, first_name, last_name, email, avatar_url')
      .eq('personal_brand_manager_id', pbmRecord.id)
      .eq('is_active', true);
    pbmClients = data || [];
  }

  // Get clients assigned to this staff as OPS
  const { data: opsClients } = await supabase
    .from('users')
    .select('id, first_name, last_name, email, avatar_url')
    .eq('ops_id', user.id)
    .eq('is_active', true);

  // Merge and deduplicate
  const allClients = [...pbmClients, ...(opsClients || [])];
  const uniqueClients = allClients.reduce((acc, client) => {
    if (!acc.some(c => c.id === client.id)) {
      acc.push(client);
    }
    return acc;
  }, [] as typeof allClients);

  // Sort by first name
  uniqueClients.sort((a, b) => (a.first_name || '').localeCompare(b.first_name || ''));

  return { data: uniqueClients, error: null };
}

/**
 * Get all conversations (for staff)
 * Uses batch RPCs for unread counts and last message to avoid N+1 queries.
 */
export async function getAllConversations(): Promise<{ data: Conversation[]; error: string | null }> {
  const { data, error } = await supabase
    .from('conversations')
    .select(`
      *,
      customer:users!conversations_customer_id_fkey(id, first_name, last_name, email, avatar_url)
    `)
    .order('last_message_at', { ascending: false, nullsFirst: false });

  if (error) return { data: [], error: error.message };

  const convs = data || [];
  if (convs.length === 0) return { data: [], error: null };

  const ids = convs.map((c) => c.id);
  const [unreadResult, lastMsgResult] = await Promise.all([
    supabase.rpc('get_unread_counts_for_conversations', { p_conversation_ids: ids }),
    supabase.rpc('get_last_messages_for_conversations', { p_conversation_ids: ids }),
  ]);

  const unreadByConvId: Record<string, number> = {};
  if (!unreadResult.error && unreadResult.data) {
    for (const row of unreadResult.data as { conversation_id: string; unread_count: number }[]) {
      unreadByConvId[row.conversation_id] = Number(row.unread_count);
    }
  }

  const lastMsgByConvId: Record<string, ChatMessage | null> = {};
  if (!lastMsgResult.error && lastMsgResult.data) {
    for (const row of lastMsgResult.data as unknown as { conversation_id: string; message_data: ChatMessage }[]) {
      lastMsgByConvId[row.conversation_id] = row.message_data ?? null;
    }
  }

  const conversationsWithDetails: Conversation[] = convs.map((conv) => ({
    ...conv,
    unread_count: unreadByConvId[conv.id] ?? 0,
    last_message: lastMsgByConvId[conv.id] ?? undefined,
  }));

  return { data: conversationsWithDetails, error: null };
}

/**
 * Get messages for a conversation (is_read/read_at reflect current user's read state from chat_message_reads).
 */
export async function getConversationMessages(
  conversationId: string,
  limit = 50,
  before?: string
): Promise<{ data: ChatMessage[]; error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: [], error: 'Not authenticated' };

  let query = supabase
    .from('chat_messages')
    .select(`
      *,
      sender:users!chat_messages_sender_id_fkey(id, first_name, last_name, avatar_url, job_title, user_roles(role)),
      attachments:chat_attachments(*)
    `)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (before) {
    query = query.lt('created_at', before);
  }

  const { data, error } = await query;

  if (error) return { data: [], error: error.message };

  const messageIds = (data || []).map((m: { id: string }) => m.id);
  const readByMe: Record<string, { read_at: string }> = {};
  if (messageIds.length > 0) {
    const { data: reads } = await supabase
      .from('chat_message_reads')
      .select('message_id, read_at')
      .eq('user_id', user.id)
      .in('message_id', messageIds);
    (reads || []).forEach((r: { message_id: string; read_at: string }) => {
      readByMe[r.message_id] = { read_at: r.read_at };
    });
  }
  
  // Get replied message IDs
  const replyIds = (data || [])
    .filter((msg: any) => msg.reply_to_message_id)
    .map((msg: any) => msg.reply_to_message_id);
  
  // Fetch replied messages if any
  let repliedMessagesMap: Record<string, any> = {};
  if (replyIds.length > 0) {
    const { data: repliedMsgs } = await supabase
      .from('chat_messages')
      .select(`
        id, content, sender_id,
        sender:users!chat_messages_sender_id_fkey(id, first_name, last_name)
      `)
      .in('id', replyIds);
    
    if (repliedMsgs) {
      repliedMessagesMap = repliedMsgs.reduce((acc: Record<string, any>, msg: any) => {
        acc[msg.id] = msg;
        return acc;
      }, {});
    }
  }
  
  const messages = (data || []).map((msg: any) => {
    const read = readByMe[msg.id];
    return {
      ...msg,
      is_read: !!read,
      read_at: read?.read_at ?? null,
      replied_message: msg.reply_to_message_id ? repliedMessagesMap[msg.reply_to_message_id] || null : null,
    };
  }).reverse() as ChatMessage[];
  
  return { data: messages, error: null };
}

/** Check if user has any non-customer role (= staff) */
function isUserStaffByRoles(roles: string[]): boolean {
  return roles.some(role => role !== 'customer');
}

// Removed isUserStaff - replaced by isUserStaffByRoles above

/**
 * Insert message using RPC for sending as another user
 */
async function insertMessageAsUser(
  conversationId: string,
  senderIdOverride: string,
  content: string
): Promise<{ messageId: string | null; error: string | null }> {
  const { data: rpcResult, error: rpcError } = await supabase.rpc('send_message_as_user', {
    p_conversation_id: conversationId,
    p_sender_id: senderIdOverride,
    p_content: content,
  });

  if (rpcError) return { messageId: null, error: rpcError.message };
  return { messageId: rpcResult, error: null };
}

/**
 * Insert message directly for own messages
 */
async function insertOwnMessage(
  conversationId: string,
  senderId: string,
  content: string,
  replyToMessageId?: string,
  sourceTicketId?: string
): Promise<{ messageId: string | null; error: string | null }> {
  const { data: insertResult, error: insertError } = await supabase
    .from('chat_messages')
    .insert({
      conversation_id: conversationId,
      sender_id: senderId,
      content,
      reply_to_message_id: replyToMessageId || null,
      source_ticket_id: sourceTicketId || null,
      sent_from_platform: detectPlatform(),
    } as any)
    .select('id')
    .single();

  if (insertError) return { messageId: null, error: insertError.message };
  return { messageId: insertResult.id, error: null };
}

/**
 * Trigger AI analysis for non-staff messages (fire-and-forget)
 */
function triggerAiAnalysisIfNotStaff(
  senderIdOverride: string | undefined,
  currentUserId: string,
  message: ChatMessage,
  conversationId: string
): void {
  if (senderIdOverride && senderIdOverride !== currentUserId) {
    return;
  }

  const senderRoles = message.sender?.user_roles?.map(r => r.role) || [];
  if (isUserStaffByRoles(senderRoles)) {
    return;
  }

  supabase.functions.invoke('analyze-chat-message', {
    body: {
      messageId: message.id,
      conversationId,
    },
  }).catch((e) => {
    console.warn('Failed to trigger AI analysis:', e);
  });
}

/**
 * Send a message
 * @param senderIdOverride - Optional: send as another user (super_admin only)
 * @param mentionedUserIds - Optional: IDs of users mentioned with @
 * @param replyToMessageId - Optional: ID of message being replied to
 * @param sourceTicketId - Optional: ID of ticket if sent from ticket interface
 */
export async function sendMessage(
  conversationId: string,
  content: string,
  attachments?: { fileName: string; fileUrl: string; fileType?: string; fileSize?: number }[],
  senderIdOverride?: string,
  mentionedUserIds?: string[],
  replyToMessageId?: string,
  sourceTicketId?: string
): Promise<{ data: ChatMessage | null; error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  // Determine the effective sender
  const senderId = senderIdOverride || user.id;

  // Insert message - use RPC for impersonation or direct insert for own messages
  const isSendingAsOther = senderIdOverride && senderIdOverride !== user.id;
  const insertResult = isSendingAsOther
    ? await insertMessageAsUser(conversationId, senderIdOverride, content)
    : await insertOwnMessage(conversationId, senderId, content, replyToMessageId, sourceTicketId);

  if (insertResult.error || !insertResult.messageId) {
    return { data: null, error: insertResult.error || 'Failed to insert message' };
  }
  const messageId = insertResult.messageId;

  // Fetch full message with sender info
  const { data: message, error: fetchError } = await supabase
    .from('chat_messages')
    .select(`
      *,
      sender:users!chat_messages_sender_id_fkey(id, first_name, last_name, avatar_url, job_title, user_roles(role))
    `)
    .eq('id', messageId)
    .single();

  if (fetchError) return { data: null, error: fetchError.message };

  // Insert attachments if any
  if (attachments && attachments.length > 0) {
    const { data: attachmentData, error: attError } = await supabase
      .from('chat_attachments')
      .insert(
        attachments.map((att) => ({
          message_id: message.id,
          file_name: att.fileName,
          file_url: att.fileUrl,
          file_type: att.fileType,
          file_size: att.fileSize,
        }))
      )
      .select();

    if (!attError) {
      (message as ChatMessage).attachments = attachmentData as ChatAttachment[];
    }
  }

  // Trigger push notification via edge function (fire-and-forget, don't block UI)
  supabase.functions.invoke('chat-notification', {
    body: { 
      conversationId, 
      messageId: message.id,
      mentionedUserIds: mentionedUserIds || [],
    },
  }).catch((e) => {
    console.warn('Failed to send push notification:', e);
  });

  // Trigger AI analysis for Task Builder (fire-and-forget, only for non-staff messages)
  // This is called from frontend since Lovable Cloud functions need auth token
  triggerAiAnalysisIfNotStaff(senderIdOverride, user.id, message as ChatMessage, conversationId);

  return { data: message as ChatMessage, error: null };
}

/**
 * Edit a message (only own messages, customers have 10 min limit)
 */
export async function editMessage(
  messageId: string,
  newContent: string,
  isStaff: boolean
): Promise<{ data: ChatMessage | null; error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: null, error: 'Not authenticated' };

  // Fetch message first to check ownership and time
  const { data: existingMsg, error: fetchError } = await supabase
    .from('chat_messages')
    .select('id, sender_id, created_at')
    .eq('id', messageId)
    .single();

  if (fetchError || !existingMsg) {
    return { data: null, error: 'Message not found' };
  }

  // Check ownership
  if (existingMsg.sender_id !== user.id) {
    return { data: null, error: 'You can only edit your own messages' };
  }

  // Check 10-minute limit for non-staff
  if (!isStaff) {
    const createdAt = new Date(existingMsg.created_at);
    const now = new Date();
    const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);
    if (diffMinutes > 10) {
      return { data: null, error: 'You can only edit messages within 10 minutes' };
    }
  }

  // Update message
  const { data: updated, error: updateError } = await supabase
    .from('chat_messages')
    .update({ content: newContent, updated_at: new Date().toISOString() })
    .eq('id', messageId)
    .select(`
      *,
      sender:users!chat_messages_sender_id_fkey(id, first_name, last_name, avatar_url, job_title, user_roles(role)),
      attachments:chat_attachments(*)
    `)
    .single();

  if (updateError) return { data: null, error: updateError.message };
  return { data: updated as ChatMessage, error: null };
}

/**
 * Delete a message (only own messages, customers have 10 min limit)
 */
export async function deleteMessage(
  messageId: string,
  isStaff: boolean
): Promise<{ error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: 'Not authenticated' };

  // Fetch message first to check ownership and time
  const { data: existingMsg, error: fetchError } = await supabase
    .from('chat_messages')
    .select('id, sender_id, created_at')
    .eq('id', messageId)
    .single();

  if (fetchError || !existingMsg) {
    return { error: 'Message not found' };
  }

  // Check ownership
  if (existingMsg.sender_id !== user.id) {
    return { error: 'You can only delete your own messages' };
  }

  // Check 10-minute limit for non-staff
  if (!isStaff) {
    const createdAt = new Date(existingMsg.created_at);
    const now = new Date();
    const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);
    if (diffMinutes > 10) {
      return { error: 'You can only delete messages within 10 minutes' };
    }
  }

  // Delete attachments first
  await supabase
    .from('chat_attachments')
    .delete()
    .eq('message_id', messageId);

  // Delete message
  const { error: deleteError } = await supabase
    .from('chat_messages')
    .delete()
    .eq('id', messageId);

  return { error: deleteError?.message || null };
}

/**
 * Check if a message can be edited/deleted by the current user
 */
export function canEditOrDeleteMessage(
  message: ChatMessage,
  userId: string | undefined,
  isStaff: boolean
): boolean {
  if (!userId || message.sender_id !== userId) return false;
  
  if (isStaff) return true;
  
  const createdAt = new Date(message.created_at);
  const now = new Date();
  const diffMinutes = (now.getTime() - createdAt.getTime()) / (1000 * 60);
  return diffMinutes <= 10;
}

/**
 * Mark messages in a conversation as read for the current user (per-user read state).
 */
export async function markMessagesAsRead(conversationId: string): Promise<{ error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { error: 'Not authenticated' };

  const { error } = await supabase.rpc('mark_conversation_read_by_me', {
    p_conversation_id: conversationId,
  });

  return { error: error?.message || null };
}

/**
 * Subscribe to new messages in a conversation
 */
export function subscribeToConversation(
  conversationId: string,
  onMessage: (message: ChatMessage) => void
) {
  const channel = supabase
    .channel(`chat:${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `conversation_id=eq.${conversationId}`,
      },
      async (payload) => {
        // Small delay to allow attachments to be inserted after the message
        // (attachments are inserted in a separate query after the message insert)
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Fetch full message with sender info and attachments
        const { data } = await supabase
          .from('chat_messages')
          .select(`
            *,
            sender:users!chat_messages_sender_id_fkey(id, first_name, last_name, avatar_url, job_title, user_roles(role)),
            attachments:chat_attachments(*)
          `)
          .eq('id', payload.new.id)
          .single();

        if (data) {
          // Fetch replied message if exists
          let replied_message = null;
          if (data.reply_to_message_id) {
            const { data: repliedMsg } = await supabase
              .from('chat_messages')
              .select(`
                id, content, sender_id,
                sender:users!chat_messages_sender_id_fkey(id, first_name, last_name)
              `)
              .eq('id', data.reply_to_message_id)
              .single();
            replied_message = repliedMsg || null;
          }
          
          onMessage({ ...data, replied_message } as ChatMessage);
        }
      }
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Subscribe to all conversations updates (for staff): new messages and per-user read marks.
 */
export function subscribeToAllConversations(
  onUpdate: () => void
) {
  const channel = supabase
    .channel('chat:all')
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'chat_messages' },
      onUpdate
    )
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_message_reads' },
      onUpdate
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

/**
 * Upload attachment to storage
 */
export async function uploadChatAttachment(
  conversationId: string,
  file: File
): Promise<{ url: string | null; error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { url: null, error: 'Not authenticated' };

  const fileExt = file.name.split('.').pop();
  const fileName = `${user.id}/${conversationId}/${Date.now()}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('chat-attachments')
    .upload(fileName, file);

  if (uploadError) return { url: null, error: uploadError.message };

  const { data: { publicUrl } } = supabase.storage
    .from('chat-attachments')
    .getPublicUrl(fileName);

  return { url: publicUrl, error: null };
}

/**
 * Get unread count for current user (customer view, per-user read state).
 */
export async function getUnreadMessageCount(): Promise<number> {
  const { data, error } = await supabase.rpc('get_my_customer_unread_count');
  if (error) return 0;

  if (typeof data === 'number') {
    return data;
  }
  if (typeof data === 'string') {
    return Number.parseInt(data, 10);
  }
  return 0;
}

/**
 * Staff member type for mentions
 */
export interface StaffMember {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string;
  avatar_url: string | null;
  job_title: string | null;
  roles: string[];
}

/**
 * Get staff members available for @ mentions (all staff - used in internal notes)
 */
export async function getStaffForMentions(): Promise<{ data: StaffMember[]; error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: [], error: 'Not authenticated' };

  // Get all staff users (all roles except 'customer')
  const { data: staffRoles, error: rolesError } = await supabase
    .from('user_roles')
    .select('user_id, role')
    .neq('role', 'customer');

  if (rolesError) return { data: [], error: rolesError.message };
  if (!staffRoles || staffRoles.length === 0) return { data: [], error: null };

  // Get unique user IDs
  const userIds = [...new Set(staffRoles.map(r => r.user_id))];

  // Get user details
  const { data: users, error: usersError } = await supabase
    .from('users')
    .select('id, first_name, last_name, email, avatar_url, job_title')
    .in('id', userIds)
    .eq('is_active', true)
    .order('first_name', { ascending: true });

  if (usersError) return { data: [], error: usersError.message };

  // Map users with their roles
  const staffMembers: StaffMember[] = (users || []).map(u => ({
    ...u,
    roles: staffRoles.filter(r => r.user_id === u.id).map(r => r.role as string),
  }));

  return { data: staffMembers, error: null };
}

/**
 * Get staff members who are participants in a specific conversation.
 * Uses message senders + assigned team of the customer.
 * Falls back to senders-only if customer profile is not readable (RLS).
 */
export async function getConversationStaffParticipants(conversationId: string): Promise<{ data: StaffMember[]; error: string | null }> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return { data: [], error: 'Not authenticated' };

  // 1. Get the customer of this conversation
  const { data: conv, error: convError } = await supabase
    .from('conversations')
    .select('customer_id')
    .eq('id', conversationId)
    .single();

  if (convError || !conv) return { data: [], error: convError?.message || 'Conversation not found' };

  const assignedUserIds = new Set<string>();

  // 2. Get staff who have sent messages in this conversation (always works via RLS)
  const { data: senders } = await supabase
    .from('chat_messages')
    .select('sender_id')
    .eq('conversation_id', conversationId)
    .neq('sender_id', conv.customer_id);

  if (senders) {
    senders.forEach(s => assignedUserIds.add(s.sender_id));
  }

  // 3. Try to get assigned staff from customer profile (may fail due to RLS — that's OK)
  const { data: customer } = await supabase
    .from('users')
    .select('personal_brand_manager_id, ops_id, video_editor_id, monteur_id')
    .eq('id', conv.customer_id)
    .maybeSingle();

  if (customer) {
    if (customer.ops_id) assignedUserIds.add(customer.ops_id);
    if (customer.video_editor_id) assignedUserIds.add(customer.video_editor_id);
    if (customer.monteur_id) assignedUserIds.add(customer.monteur_id);

    // PBM is a reference to team_personal_brand_managers.id, resolve to user_id
    if (customer.personal_brand_manager_id) {
      const { data: pbm } = await supabase
        .from('team_personal_brand_managers')
        .select('user_id')
        .eq('id', customer.personal_brand_manager_id)
        .single();
      if (pbm?.user_id) assignedUserIds.add(pbm.user_id);
    }
  }

  // 4. Also add the current user (they should always be able to see themselves)
  assignedUserIds.add(user.id);

  if (assignedUserIds.size === 0) return { data: [], error: null };

  // 5. Get user details + roles for all participant IDs
  const participantIds = [...assignedUserIds];
  
  const [usersResult, rolesResult] = await Promise.all([
    supabase
      .from('users')
      .select('id, first_name, last_name, email, avatar_url, job_title')
      .in('id', participantIds)
      .eq('is_active', true)
      .order('first_name', { ascending: true }),
    supabase
      .from('user_roles')
      .select('user_id, role')
      .in('user_id', participantIds)
      .neq('role', 'customer'),
  ]);

  if (usersResult.error) return { data: [], error: usersResult.error.message };

  const staffMembers: StaffMember[] = (usersResult.data || []).map(u => ({
    ...u,
    roles: (rolesResult.data || []).filter(r => r.user_id === u.id).map(r => r.role as string),
  }));

  // Filter out current user from mention suggestions (you don't mention yourself)
  return { data: staffMembers.filter(s => s.id !== user.id), error: null };
}

/**
 * Get total unread count across all assigned conversations (for staff, per-user read state).
 */
export async function getStaffTotalUnreadCount(): Promise<number> {
  const { data, error } = await supabase.rpc('get_my_staff_total_unread_count');
  if (error) return 0;

  if (typeof data === 'number') {
    return data;
  }
  if (typeof data === 'string') {
    return Number.parseInt(data, 10);
  }
  return 0;
}

/**
 * Subscribe to unread count changes for staff (new messages and per-user read marks).
 */
export function subscribeToStaffUnreadCount(
  onUpdate: (count: number) => void
) {
  getStaffTotalUnreadCount().then(onUpdate);

  const channel = supabase
    .channel('staff-unread')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_messages' },
      () => getStaffTotalUnreadCount().then(onUpdate)
    )
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_message_reads' },
      () => getStaffTotalUnreadCount().then(onUpdate)
    )
    .subscribe();

  return () => supabase.removeChannel(channel);
}

/**
 * Subscribe to unread count changes for customers (new messages and read marks).
 */
export function subscribeToCustomerUnreadCount(
  onUpdate: (count: number) => void
) {
  getUnreadMessageCount().then(onUpdate);

  const channel = supabase
    .channel('customer-unread')
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_messages' },
      () => getUnreadMessageCount().then(onUpdate)
    )
    .on(
      'postgres_changes',
      { event: 'INSERT', schema: 'public', table: 'chat_message_reads' },
      () => getUnreadMessageCount().then(onUpdate)
    )
    .subscribe();

  return () => supabase.removeChannel(channel);
}
